// routeStringExpand — parse a CIFP-style route string into classified
// tokens, then resolve the resolvable tokens to PlanPoints via the
// search API. Used by Route Advisor v2 to splice a published or
// cleared route directly into the flight plan.
//
// Token shapes we honor:
//   - 3-char alpha           : VOR/navaid (SFO, JFK, ENI)
//   - 5-char alpha           : 5-letter waypoint / intersection (DIMGE, FOBRO, MERIT)
//   - 4-char alpha           : 4-letter intersection (rare but legal)
//   - 4-char w/ leading K    : airport ICAO (KSFO) — drop if matches origin/dest
//   - 3-char alphanumeric    : LID airport (O46) — drop if matches origin/dest
//   - V<digits> / J<digits>  : Victor / Jet airway — SKIP (it's the path between
//                              the surrounding fixes, not a waypoint)
//   - T<digits> / Q<digits>  : RNAV T-route / Q-route — SKIP
//   - 5+ char ending in digit: SID/STAR/IAP procedure ident (WOONS2, FEVTA1,
//                              ROBUC3) — SKIP. The procedure picker handles
//                              SID/STAR/IAP separately; pre-loading them via
//                              the route string would require expanding their
//                              CIFP legs which is the procedure picker's job.
//   - Other / unknown        : SKIP (best effort; pilot can manually add)

export type TokenKind =
  | 'origin'        // matched the route's origin airport; skip
  | 'destination'   // matched the route's destination airport; skip
  | 'fix'           // 4 / 5 / 3-char waypoint or navaid to resolve via search
  | 'airway'        // V6 / J55 / T315 / Q12 etc. — skip
  | 'procedure'     // WOONS2 / ROBUC3 — skip (use procedure picker)
  | 'unknown';      // not recognised; skip

export interface ClassifiedToken {
  raw: string;
  kind: TokenKind;
  /** Normalised ident for search lookup (uppercased, no K-strip yet). */
  ident: string;
}

const AIRWAY_RE       = /^[VJTQ]\d{1,4}$/;       // V6, J55, T315, Q12
const PROCEDURE_RE    = /^[A-Z]{3,5}\d$/;        // ROBUC3, WOONS2, FEVTA1, AKLAM5
const FIX5_RE         = /^[A-Z]{5}$/;            // DIMGE, FOBRO, MERIT
const FIX4_RE         = /^[A-Z]{4}$/;            // Rare 4-letter intersection
const NAVAID3_RE      = /^[A-Z]{3}$/;            // SFO, JFK, ENI
const AIRPORT_K4_RE   = /^K[A-Z]{3}$/;           // KSFO, KJFK, KSTS
const AIRPORT_LID_RE  = /^[A-Z0-9]{3,4}$/;       // O46, 1L8, X51 — pre-checks pass for navaid3 too

/** Strip a leading 'K' on 4-char US idents so KO46 and O46 normalise.
 *  Mirrors the same helper in procedureExpansion and import-prefroutes. */
export function normIdent(s: string): string {
  const t = (s || '').trim().toUpperCase();
  if (t.length === 4 && t.startsWith('K')) return t.slice(1);
  return t;
}

/** Classify a single token in the context of an origin/destination pair.
 *  Both idents may be in either FAA or ICAO form (normalised via normIdent). */
export function classifyToken(
  raw: string,
  originIdent: string,
  destinationIdent: string,
): ClassifiedToken {
  const t = (raw || '').trim().toUpperCase();
  if (!t) return { raw, kind: 'unknown', ident: '' };

  const o = normIdent(originIdent);
  const d = normIdent(destinationIdent);
  const tn = normIdent(t);

  if (tn === o) return { raw, kind: 'origin', ident: tn };
  if (tn === d) return { raw, kind: 'destination', ident: tn };

  if (AIRWAY_RE.test(t)) return { raw, kind: 'airway', ident: t };
  if (PROCEDURE_RE.test(t)) return { raw, kind: 'procedure', ident: t };

  // Order matters: a token like "ENI" matches NAVAID3_RE AND AIRPORT_LID_RE.
  // We prefer the fix/navaid interpretation since published route strings
  // overwhelmingly cite navaids, not airports as intermediate fixes.
  if (FIX5_RE.test(t) || FIX4_RE.test(t) || NAVAID3_RE.test(t)) {
    return { raw, kind: 'fix', ident: t };
  }

  // 4-char K-prefixed airport that isn't origin/dest — treat as a
  // resolvable waypoint (could be a fly-over airport). Defer to the
  // search lookup; if it doesn't resolve, the resolver drops it.
  if (AIRPORT_K4_RE.test(t)) return { raw, kind: 'fix', ident: t };

  // LID airport like O46 / 1L8 — same treatment.
  if (AIRPORT_LID_RE.test(t)) return { raw, kind: 'fix', ident: t };

  return { raw, kind: 'unknown', ident: t };
}

/** Tokenize a route string and classify each token.
 *
 *  Whitespace-separated. Trims and uppercases each token. Multiple
 *  consecutive whitespace characters collapse to one split.
 *
 *  The classified array preserves token ORDER, including skip-tagged
 *  tokens (airways / procedures / origin / destination). The caller
 *  filters for the kinds it wants to resolve. */
export function tokenizeAndClassify(
  routeString: string,
  originIdent: string,
  destinationIdent: string,
): ClassifiedToken[] {
  const tokens = (routeString || '')
    .trim()
    .split(/\s+/)
    .filter((t) => t.length > 0);
  return tokens.map((t) => classifyToken(t, originIdent, destinationIdent));
}

/** Return just the tokens that need a search-API resolution to become
 *  PlanPoints, in route order. */
export function resolvableTokens(
  classified: ClassifiedToken[],
): ClassifiedToken[] {
  return classified.filter((t) => t.kind === 'fix');
}

// ---- Airway expansion helpers ----------------------------------------

/**
 * Describes one [fix → airway → fix] triplet that can be expanded via
 * GET /api/airway?ident=X&from=A&to=B.
 */
export interface AirwayExpansion {
  /** Index in the classified array of the airway token. */
  airwayIdx: number;
  /** Airway ident, e.g. "V6" or "J55". */
  airwayIdent: string;
  /** Ident of the fix/navaid immediately before the airway (entry). */
  fromIdent: string;
  /** Ident of the fix/navaid immediately after the airway (exit). */
  toIdent: string;
}

/**
 * Scan a classified token list and identify every [fix, airway, fix]
 * triplet that the Route Advisor should expand by calling
 * GET /api/airway?ident=X&from=A&to=B.
 *
 * Pure function — no side effects, no I/O. The caller is responsible
 * for fetching the airway data and splicing in the intermediate fixes.
 *
 * When consecutive airways share the same entry/exit fix (rare edge
 * case), both are returned independently; the caller decides which to
 * expand first.
 */
export function findAirwayExpansions(classified: ClassifiedToken[]): AirwayExpansion[] {
  const results: AirwayExpansion[] = [];
  for (let i = 1; i < classified.length - 1; i++) {
    const prev = classified[i - 1];
    const curr = classified[i];
    const next = classified[i + 1];
    // A resolvable entry + airway + resolvable exit is an expansion candidate.
    // We accept 'fix' on either side; origin/destination are NOT valid
    // flanking tokens (they would mean the airway starts/ends at an airport,
    // which can happen but the origin/dest are already in the plan as
    // PlanPoints so intermediate fixes are still useful).
    if (
      curr.kind === 'airway' &&
      (prev.kind === 'fix' || prev.kind === 'origin' || prev.kind === 'destination') &&
      (next.kind === 'fix' || next.kind === 'origin' || next.kind === 'destination')
    ) {
      results.push({
        airwayIdx: i,
        airwayIdent: curr.ident,
        fromIdent: prev.ident,
        toIdent: next.ident,
      });
    }
  }
  return results;
}
